import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

// 기본 제공 태그 목록 - 컴포넌트 외부에서 정의하여 불변성 보장
const DEFAULT_TAGS = [
  '업무', '개인', '중요', '임시', '공유',
  '소셜미디어', '이메일', '쇼핑', '금융', '엔터테인먼트'
];

/**
 * 태그 선택/추가 컴포넌트
 * @param {Array} selectedTags - 선택된 태그 목록
 * @param {Function} onTagsChange - 태그 변경 핸들러
 * @param {Array} availableTags - 사용 가능한 태그 목록
 * @param {boolean} allowAddNew - 새 태그 추가 허용 여부
 * @param {number} maxTags - 최대 태그 개수
 */
function TagSelector({ 
  selectedTags = [], 
  onTagsChange, 
  availableTags = [], 
  allowAddNew = true, 
  maxTags = 10 
}) {
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef(null);

  // 전체 사용 가능한 태그 (기본 태그 + 제공된 태그) - useMemo로 최적화
  const allAvailableTags = useMemo(() => {
    return [...new Set([...DEFAULT_TAGS, ...availableTags])];
  }, [availableTags]);

  // 깔끔한 해결책: 직접 의존성 사용
  const filteredTags = useMemo(() => {
    if (inputValue.trim()) {
      return allAvailableTags.filter(tag =>
        tag.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.includes(tag)
      );
    } else {
      return allAvailableTags.filter(tag => !selectedTags.includes(tag));
    }
  }, [inputValue, allAvailableTags, selectedTags]); // 직접 의존성 사용

  // 태그 추가 핸들러 - useCallback으로 최적화
  const handleAddTag = useCallback((tag) => {
    if (!tag.trim()) return;
    
    const trimmedTag = tag.trim();
    
    // 중복 체크
    if (selectedTags.includes(trimmedTag)) {
      setErrorMessage(`"${trimmedTag}" 태그가 이미 추가되어 있습니다.`);
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    // 최대 개수 체크
    if (selectedTags.length >= maxTags) {
      setErrorMessage(`최대 ${maxTags}개의 태그만 추가할 수 있습니다.`);
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    // 에러 메시지 클리어
    setErrorMessage('');
    
    const newTags = [...selectedTags, trimmedTag];
    if (onTagsChange) {
      onTagsChange(newTags);
    }
    
    setInputValue('');
    setIsDropdownOpen(false);
  }, [selectedTags, maxTags, onTagsChange]);

  // 태그 제거 핸들러 - useCallback으로 최적화
  const handleRemoveTag = useCallback((tagToRemove) => {
    const newTags = selectedTags.filter(tag => tag !== tagToRemove);
    if (onTagsChange) {
      onTagsChange(newTags);
    }
  }, [selectedTags, onTagsChange]);

  // 입력 변경 핸들러 - useCallback으로 최적화
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsDropdownOpen(true);
  }, []);

  // 키보드 입력 핸들러 - useCallback으로 최적화
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim() && allowAddNew) {
        handleAddTag(inputValue);
      } else if (filteredTags.length > 0) {
        handleAddTag(filteredTags[0]);
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      // 입력값이 없을 때 백스페이스로 마지막 태그 제거
      handleRemoveTag(selectedTags[selectedTags.length - 1]);
    }
  }, [inputValue, allowAddNew, filteredTags, selectedTags, handleAddTag, handleRemoveTag]);

  // 드롭다운 외부 클릭시 닫기 - useCallback으로 최적화
  const handleClickOutside = useCallback((event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="tag-selector">
      <div className="tag-selector-container">
        {/* 선택된 태그들 */}
        <div className="selected-tags">
          {selectedTags.map((tag, index) => (
            <span key={index} className="selected-tag">
              <span className="selected-tag-text">{tag}</span>
              <button
                className="selected-tag-remove"
                onClick={() => handleRemoveTag(tag)}
                title={`${tag} 태그 제거`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>

        {/* 태그 입력 필드 */}
        <div className="tag-input-container" ref={inputRef}>
          <input
            type="text"
            className="tag-input"
            placeholder={selectedTags.length === 0 ? "태그를 선택하거나 입력하세요..." : "추가 태그 입력..."}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsDropdownOpen(true)}
          />

          {/* 태그 드롭다운 */}
          {isDropdownOpen && (
            <div className="tag-dropdown">
              {filteredTags.length > 0 ? (
                <>
                  {filteredTags.slice(0, 8).map((tag, index) => (
                    <button
                      key={index}
                      className="tag-dropdown-item"
                      onClick={() => handleAddTag(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </>
              ) : inputValue.trim() && allowAddNew ? (
                <button
                  className="tag-dropdown-item tag-dropdown-new"
                  onClick={() => handleAddTag(inputValue)}
                >
                  <span className="tag-new-icon">+</span>
                  "{inputValue}" 새 태그로 추가
                </button>
              ) : (
                <div className="tag-dropdown-empty">
                  사용 가능한 태그가 없습니다
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 태그 정보 및 에러 메시지 */}
      <div className="tag-selector-info">
        {errorMessage ? (
          <span className="tag-error-message" style={{ color: '#dc3545', fontSize: '12px' }}>
            {errorMessage}
          </span>
        ) : (
          <>
            <span className="tag-count">
              선택된 태그: {selectedTags.length}/{maxTags}개
            </span>
            {allowAddNew && (
              <span className="tag-help">
                💡 Enter로 추가, Backspace로 제거
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// React.memo로 컴포넌트 최적화 - props가 변경되지 않으면 리렌더링 방지
export default React.memo(TagSelector);